import {
  computed,
  DestroyRef,
  effect,
  inject,
  Injectable,
  signal,
} from '@angular/core';
import { UserUiPreferencesService } from './user-ui-preferences.service';

export type ThemePreference = 'light' | 'dark' | 'system';

const THEME_KEY = 'theme';
const DARK_THEME_CLASS = 'theme-dark';
const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)';

const isThemePreference = (value: unknown): value is ThemePreference =>
  value === 'light' || value === 'dark' || value === 'system';

/**
 * Light/dark appearance, stored on the user; applied as the `theme-dark` class
 * on `<html>` (also PrimeNG's `darkModeSelector`).
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly uiPreferences = inject(UserUiPreferencesService);

  private readonly darkSchemeQuery = window.matchMedia(DARK_SCHEME_QUERY);
  private readonly _systemPrefersDark = signal(this.darkSchemeQuery.matches);
  readonly systemPrefersDark = this._systemPrefersDark.asReadonly();

  /** `system` until `me()` resolves, and on public pages where there is no user. */
  readonly preference = computed<ThemePreference>(() => {
    const stored = this.uiPreferences.snapshot()[THEME_KEY];
    return isThemePreference(stored) ? stored : 'system';
  });

  readonly isDark = computed(() => {
    const preference = this.preference();
    return preference === 'system'
      ? this._systemPrefersDark()
      : preference === 'dark';
  });

  constructor() {
    const onSchemeChange = (event: MediaQueryListEvent) =>
      this._systemPrefersDark.set(event.matches);

    this.darkSchemeQuery.addEventListener('change', onSchemeChange);
    inject(DestroyRef).onDestroy(() =>
      this.darkSchemeQuery.removeEventListener('change', onSchemeChange),
    );

    effect(() => {
      document.documentElement.classList.toggle(
        DARK_THEME_CLASS,
        this.isDark(),
      );
    });
  }

  /** Rejects if the write fails, leaving the caller to report it. */
  setPreference(preference: ThemePreference): Promise<void> {
    return this.uiPreferences.patch({ [THEME_KEY]: preference });
  }
}
